/**
 * Domain enumerations.
 *
 * These are the *stable, code-level* enumerations — role, status, slot type. Anything a
 * chamber might reasonably want to add without a deploy (document types, rejection
 * reasons, supporting-document categories) is master data in the database, not an enum
 * here. Spec §2 is explicit about that: "a controlled, extensible lookup (not a
 * hard-coded enum)".
 */
import { z } from 'zod';
/** Who someone is. Determines blast radius, so it is deliberately small and closed. */
export const UserRole = {
    PLATFORM_SUPER_ADMIN: 'PLATFORM_SUPER_ADMIN',
    CHAMBER_ADMIN: 'CHAMBER_ADMIN',
    CHAMBER_OPERATOR: 'CHAMBER_OPERATOR',
    EXPORTER_USER: 'EXPORTER_USER',
};
export const userRoleSchema = z.nativeEnum(UserRole);
/** Roles that operate inside a single chamber. */
export const CHAMBER_SCOPED_ROLES = [
    UserRole.CHAMBER_ADMIN,
    UserRole.CHAMBER_OPERATOR,
];
/**
 * The certificate request lifecycle (spec §15).
 *
 *   AWAITING_ACTION ──approve──▶ APPROVED ──revoke──▶ REVOKED
 *          │                                              │
 *          ├──reject───────────▶ REJECTED                 └─▶ (linked reissue re-enters
 *          │                                                   AWAITING_ACTION)
 *          └──clarify──▶ CLARIFICATION_REQUESTED ──respond──▶ AWAITING_ACTION
 */
export const DocumentStatus = {
    AWAITING_ACTION: 'AWAITING_ACTION',
    CLARIFICATION_REQUESTED: 'CLARIFICATION_REQUESTED',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
    REVOKED: 'REVOKED',
};
export const documentStatusSchema = z.nativeEnum(DocumentStatus);
export const TERMINAL_STATUSES = [
    DocumentStatus.APPROVED,
    DocumentStatus.REJECTED,
    DocumentStatus.REVOKED,
];
/** Statuses an operator can still act on from the queue. */
export const ACTIONABLE_STATUSES = [DocumentStatus.AWAITING_ACTION];
export const SubmissionType = {
    ORIGINAL: 'ORIGINAL',
    AMENDMENT: 'AMENDMENT',
};
export const submissionTypeSchema = z.nativeEnum(SubmissionType);
/**
 * Carried through from the legacy system's reporting, which distinguishes an original
 * issuance from a reissue. Set automatically: an AMENDMENT submission produces REISSUE.
 */
export const MessageType = {
    ORIGINAL: 'ORIGINAL',
    REISSUE: 'REISSUE',
};
export const messageTypeSchema = z.nativeEnum(MessageType);
/**
 * How a certificate request reached us (spec v4 §23).
 *
 * Not to be confused with `DeliveryMode`, immediately below, which is how the finished
 * certificate is dispatched. Arrival and dispatch answer opposite questions.
 */
export const IntakeChannel = {
    INTEGRATOR: 'INTEGRATOR',
    UPLOAD: 'UPLOAD',
    EMAIL: 'EMAIL',
};
export const intakeChannelSchema = z.nativeEnum(IntakeChannel);
/** What an operator reads in the column that answers "where did this come from?". */
export const INTAKE_CHANNEL_LABEL = {
    INTEGRATOR: 'Integrator',
    UPLOAD: 'Upload',
    EMAIL: 'Email',
};
export function intakeChannelLabel(channel) {
    return INTAKE_CHANNEL_LABEL[channel] ?? channel;
}
export const DeliveryMode = {
    ELECTRONIC: 'ELECTRONIC',
    PRINTED: 'PRINTED',
    COURIER: 'COURIER',
    PICKUP: 'PICKUP',
};
export const deliveryModeSchema = z.nativeEnum(DeliveryMode);
/**
 * What a stamp slot places on the page (spec §7.6.2). A document type may declare any
 * number of slots of any of these types, including several SEALs — some FTA certificates
 * carry a chamber seal plus a secondary certifying-body seal.
 */
export const StampSlotType = {
    SEAL: 'SEAL',
    SIGNATURE: 'SIGNATURE',
    CERTIFICATE_NUMBER: 'CERTIFICATE_NUMBER',
    DATE: 'DATE',
    DESIGNATION: 'DESIGNATION',
    SIGNATORY_NAME: 'SIGNATORY_NAME',
    ISSUED_AT: 'ISSUED_AT',
    ISSUED_AT_LINE: 'ISSUED_AT_LINE',
    FREE_TEXT: 'FREE_TEXT',
};
export const stampSlotTypeSchema = z.nativeEnum(StampSlotType);
/** Slot types whose content is an image composited onto the page. */
export const IMAGE_SLOT_TYPES = [
    StampSlotType.SEAL,
    StampSlotType.SIGNATURE,
];
export const TextAlign = { LEFT: 'LEFT', CENTER: 'CENTER', RIGHT: 'RIGHT' };
export const textAlignSchema = z.nativeEnum(TextAlign);
/** How often the certificate number sequence restarts (spec §7.6.2b). */
export const SequenceResetCadence = {
    NEVER: 'NEVER',
    YEARLY: 'YEARLY',
    MONTHLY: 'MONTHLY',
};
export const sequenceResetCadenceSchema = z.nativeEnum(SequenceResetCadence);
/**
 * Whether a chamber's certificate numbers run as one sequence for the whole chamber, or
 * one per document type (D-011).
 *
 * CHAMBER is the default because certificate numbers must be unique per chamber, and two
 * per-type counters with no distinguishing prefix collide on the second issuance. The
 * real BCQ certificates run one shared sequence across four different FTA forms.
 */
export const SequenceScope = {
    CHAMBER: 'CHAMBER',
    DOCUMENT_TYPE: 'DOCUMENT_TYPE',
};
export const sequenceScopeSchema = z.nativeEnum(SequenceScope);
export const RegistrationStatus = {
    SUBMITTED: 'SUBMITTED',
    INFO_REQUESTED: 'INFO_REQUESTED',
    INFO_PROVIDED: 'INFO_PROVIDED',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
};
export const registrationStatusSchema = z.nativeEnum(RegistrationStatus);
export const ApplicantType = {
    EXPORTER: 'EXPORTER',
    FREIGHT_FORWARDER: 'FREIGHT_FORWARDER',
};
export const applicantTypeSchema = z.nativeEnum(ApplicantType);
/** Outcome of one completeness/consistency check (spec §8). Advisory — never gating. */
export const CheckStatus = {
    PASS: 'PASS',
    WARNING: 'WARNING',
    FAIL: 'FAIL',
    UNAVAILABLE: 'UNAVAILABLE',
};
export const checkStatusSchema = z.nativeEnum(CheckStatus);
/** Which engine produced a check result — the panel is explicit about this. */
export const CheckSource = {
    RULE: 'RULE',
    LLM: 'LLM',
};
export const checkSourceSchema = z.nativeEnum(CheckSource);
/** Master data list kinds — one table, keyed by list type (spec §13.3). */
export const MasterDataListType = {
    REJECTION_REASON: 'REJECTION_REASON',
    CLARIFICATION_ITEM: 'CLARIFICATION_ITEM',
    SUPPORTING_DOCUMENT_CATEGORY: 'SUPPORTING_DOCUMENT_CATEGORY',
    MODIFICATION_REQUEST_TYPE: 'MODIFICATION_REQUEST_TYPE',
    REGISTRATION_REJECTION_REASON: 'REGISTRATION_REJECTION_REASON',
};
export const masterDataListTypeSchema = z.nativeEnum(MasterDataListType);
export const IntegratorStatus = {
    ACTIVE: 'ACTIVE',
    SUSPENDED: 'SUSPENDED',
    ARCHIVED: 'ARCHIVED',
};
export const integratorStatusSchema = z.nativeEnum(IntegratorStatus);
export const ApiKeyEnvironment = { LIVE: 'LIVE', TEST: 'TEST' };
export const apiKeyEnvironmentSchema = z.nativeEnum(ApiKeyEnvironment);
/**
 * Integration API scopes (spec v3 §20.4). A key's effective permission is the
 * intersection of its integrator's chamber grants and its own scope narrowing — a key
 * can only ever be narrower than its integrator, never broader.
 */
export const ApiScope = {
    CERTIFICATE_REQUESTS_SUBMIT: 'certificate-requests:submit',
    CERTIFICATE_REQUESTS_READ: 'certificate-requests:read',
    CERTIFICATE_REQUESTS_ACK: 'certificate-requests:ack',
    CERTIFICATE_REQUESTS_RESPOND_CLARIFICATION: 'certificate-requests:respond-clarification',
    ATTACHMENTS_WRITE: 'attachments:write',
    DOCUMENT_TYPES_READ: 'document-types:read',
    VERIFY_READ: 'verify:read',
};
export const apiScopeSchema = z.nativeEnum(ApiScope);
export const ALL_API_SCOPES = Object.values(ApiScope);
export const GrantOrganisationScope = { ALL: 'ALL', SPECIFIC: 'SPECIFIC' };
export const grantOrganisationScopeSchema = z.nativeEnum(GrantOrganisationScope);
/** Webhook event names, as published to integrators. Additive only — never renamed. */
export const WebhookEvent = {
    CERTIFICATE_RECEIVED: 'certificate.received',
    CERTIFICATE_APPROVED: 'certificate.approved',
    CERTIFICATE_REJECTED: 'certificate.rejected',
    CERTIFICATE_REVOKED: 'certificate.revoked',
    CERTIFICATE_CLARIFICATION_REQUESTED: 'certificate.clarification_requested',
};
export const webhookEventSchema = z.nativeEnum(WebhookEvent);
export const ALL_WEBHOOK_EVENTS = Object.values(WebhookEvent);
export const WebhookDeliveryStatus = {
    PENDING: 'PENDING',
    SUCCEEDED: 'SUCCEEDED',
    FAILED: 'FAILED',
    DEAD_LETTERED: 'DEAD_LETTERED',
};
export const webhookDeliveryStatusSchema = z.nativeEnum(WebhookDeliveryStatus);
/** Public verification outcome — deliberately coarse. Nothing else is disclosed. */
export const VerificationResult = {
    VALID: 'VALID',
    REVOKED: 'REVOKED',
    SUPERSEDED: 'SUPERSEDED',
    NOT_FOUND: 'NOT_FOUND',
};
export const verificationResultSchema = z.nativeEnum(VerificationResult);
//# sourceMappingURL=enums.js.map